import {Checkbox, Field, Input, Label} from '@zendeskgarden/react-forms';
import {Col, Row} from '@zendeskgarden/react-grid';
import {ReactElement, useEffect, useMemo, useRef, useState} from "react";
import styles from '../AnalystPayrollDrawer.module.scss';

import {Button} from "@zendeskgarden/react-buttons";
import {CustomZDDropdown} from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {MONTHS} from "../../../constants/Months";
import {TListPayrollPositions} from "../../../contexts/account/data/utils";
import {Property, useProperties} from "../../../contexts/properties/PropertiesContext";
import {TLockedProperties} from "../../../pages/bulk-update/BulkUpdate";
import {getAcquisitionPropertyVersionForDisplayDate} from "../../../pages/workflows/account/formula-bar/logic/utils";
import PayrollDatePicker from "../components/PayrollDatePicker";
import {ensureDateInLocale} from "../date-helpers";
import {
    AllEmployeeClassifications,
    AllPayrollModeledEmployeeTypes,
    LocalPayrollEmployeeModel,
    LocalPayrollEmployeeModelKeys
} from "../helpers/types";


interface FormPropertyAttribution {
    propertyId: string | undefined;
    attributionPercentage: string | undefined;
}

interface PositionalDetailsProps {
    propertiesData: Property[]
    employeeData: LocalPayrollEmployeeModel,
    payrollPositionsData: TListPayrollPositions,
    updateFormState: (key: LocalPayrollEmployeeModelKeys, value: any) => void,
    lockedProperties: TLockedProperties,
}

export default function TabPositionDetails(
    {
        propertiesData,
        employeeData,
        payrollPositionsData,
        updateFormState,
        lockedProperties,
    }: PositionalDetailsProps): ReactElement {
    const [propertyAttributions, setPropertyAttributions] = useState<FormPropertyAttribution[]>(employeeData?.properties ? employeeData.properties.map(p => ({
        propertyId: p.propertyId,
        attributionPercentage: p.attributionPercentage !== null ? p.attributionPercentage : undefined
    })) : [{
        propertyId: undefined,
        attributionPercentage: undefined
    }]);
    const {currentProperty} = useProperties();
    const employeeLocked = employeeData.propertyLocked === true;

    // these variables are only used in acquisition property month picker
    const startPeriodTriggerRef = useRef<HTMLParagraphElement>(null);
    const [startPeriodRefElement, setStartPeriodRefElement] = useState<HTMLParagraphElement | null>(null);
    const endPeriodTriggerRef = useRef<HTMLParagraphElement>(null);
    const [endPeriodRefElement, setEndPeriodRefElement] = useState<HTMLParagraphElement | null>(null);
    const [minMonth, setMinMonth] = useState<number>(0);
    const [minYear, setMinYear] = useState<number>(1986);
    const [maxMonth, setMaxMonth] = useState<number>(11);
    const [maxYear, setMaxYear] = useState<number>(2100);
    const [startMonth, setStartMonth] = useState<number>(0);
    const [startYear, setStartYear] = useState<number>(1986);
    const [endMonth, setEndMonth] = useState<number>(11);
    const [endYear, setEndYear] = useState<number>(2100);

    const startDateText = useMemo<string>(() => {
        if (currentProperty?.isAcquisitionProperty) {
            const displayYear = getAcquisitionPropertyVersionForDisplayDate(startMonth, startYear, currentProperty);

            return `${MONTHS[startMonth]} ${displayYear}`;
        }
        return "";
    }, [startMonth, startYear, currentProperty]);

    const endDateText = useMemo(() => {
        if (currentProperty?.isAcquisitionProperty) {
            const displayYear = getAcquisitionPropertyVersionForDisplayDate(endMonth, endYear, currentProperty);

            return `${MONTHS[endMonth]} ${displayYear}`;
        }
        return "";
    }, [endMonth, endYear, currentProperty]);

    useEffect(() => {
        if (currentProperty?.isAcquisitionProperty &&
            currentProperty.acquisitionBudgetYear != null &&
            currentProperty.acquisitionBudgetStartMonth != null
        ) {
            const defaultMinMonth = currentProperty.acquisitionBudgetStartMonth;
            const defaultMinYear = defaultMinMonth == 0 ? currentProperty.acquisitionBudgetYear - 2 : currentProperty.acquisitionBudgetYear - 1;
            const defaultMaxMonth = defaultMinMonth == 0 ? 11 : defaultMinMonth - 1;
            const defaultMaxYear = defaultMinMonth == 0 ? currentProperty.acquisitionBudgetYear : currentProperty.acquisitionBudgetYear + 1;

            setMinMonth(defaultMinMonth);
            setMinYear(defaultMinYear);
            setMaxMonth(defaultMaxMonth);
            setMaxYear(defaultMaxYear);

            const startDate = ensureDateInLocale(employeeData.startDate);
            const endDate = ensureDateInLocale(employeeData.terminationDate);

            if (startDate && endDate) {
                setStartMonth(startDate.getMonth());
                setStartYear(startDate.getFullYear());
                setEndMonth(endDate.getMonth());
                setEndYear(endDate.getFullYear());
            } else {
                setStartMonth(defaultMinMonth);
                setStartYear(defaultMinYear);
                setEndMonth(defaultMaxMonth);
                setEndYear(defaultMaxYear);
            }

            setPropertyAttributions([
                {
                    propertyId: currentProperty.id,
                    attributionPercentage: "100",
                }
            ]);
            updateGlobalStateWithValid([
                {
                    propertyId: currentProperty.id,
                    attributionPercentage: "100",
                }
            ]);
        }
    }, [currentProperty]);

    if (!employeeData || !payrollPositionsData) {
        return <></>;
    }

    function updateGlobalStateWithValid(newAllocations: FormPropertyAttribution[]): void {
        const valid = newAllocations.filter(pa => !!pa.propertyId);
        updateFormState(LocalPayrollEmployeeModelKeys.properties, valid);
    }

    function updatePropertyAllocationUnitCount(unitCountAllocation: boolean): void {
        updateFormState(LocalPayrollEmployeeModelKeys.propertyAttributionUnitCount, unitCountAllocation);
    }

    function addPropertyAllocation(): void {
        setPropertyAttributions([
            ...propertyAttributions,
            {
                propertyId: undefined,
                attributionPercentage: undefined
            }
        ]);
    }

    function removePropertyAllocation(idx: number): void {
        const remaining = [
            ...propertyAttributions.slice(0, idx),
            ...propertyAttributions.slice(idx + 1)
        ];
        // We do this to ensure 100% allocation when only 1 property is selected.
        if(remaining.length === 1 && remaining[0]) {
            remaining[0].attributionPercentage = undefined;
        }
        setPropertyAttributions(remaining);
        updateGlobalStateWithValid(remaining);
    }

    function setPropertyId(idx: number, id: string): void {
        const newArr = [...propertyAttributions];
        const selected = newArr[idx];
        if(selected) {
            selected.propertyId = id;
        }
        setPropertyAttributions(newArr);
        updateGlobalStateWithValid(newArr);
    }

    function setAllocationPercentage(idx: number, percentage: string): void {
        const newArr = [...propertyAttributions];
        const selected = newArr[idx];
        if(selected) {
            selected.attributionPercentage = percentage !== "" ? percentage : undefined;
        }
        setPropertyAttributions(newArr);
        updateGlobalStateWithValid(newArr);
    }

    return (
        <div className={styles.positionDetailsContainer}>
            <div style={{maxWidth: "400px"}}>
                <h4 className={styles.positionDetailsHeader}>Position Details</h4>
                <Field className={styles.field}>
                    <Label className={styles.label}>Status</Label>
                    <CustomZDDropdown
                        applySelectedItems={(items) => {
                            updateFormState(LocalPayrollEmployeeModelKeys.type, Array.isArray(items) ? items[0] : items);
                        }}
                        initialSelectedItems={employeeData.type ? [employeeData.type] : undefined}
                        openDropdownPlaceholder="Select Status"
                        closedDropdownPlaceholder=""
                        options={AllPayrollModeledEmployeeTypes.map(opt => ({value: opt.value, label: opt.label}))}
                        isError={employeeData.typeTouched && employeeData.type === undefined ? true : undefined}
                        onBlur={() => {
                            return undefined;
                        }}
                        noSearchIcon
                        disabled={employeeLocked}
                    />
                </Field>

                <Field className={styles.field}>
                    <Label className={styles.label}>Position Type</Label>
                    <CustomZDDropdown
                        applySelectedItems={(items) => {
                            const newPositionId: string = Array.isArray(items) ? items[0] : items;
                            updateFormState(LocalPayrollEmployeeModelKeys.position, payrollPositionsData.find(p => p.id === newPositionId));
                        }}
                        initialSelectedItems={employeeData.position ? [employeeData.position.id] : undefined}
                        openDropdownPlaceholder="Select Position"
                        closedDropdownPlaceholder=""
                        options={payrollPositionsData.map(opt => ({value: opt.id, label: opt.name})).sort((a, b) => a.label.localeCompare(b.label))}
                        isError={employeeData.positionTouched && employeeData.position === undefined ? true : undefined}
                        onBlur={() => {
                            return undefined;
                        }}
                        noSearchIcon
                        disabled={employeeLocked}
                    />
                </Field>

                <Field className={styles.field}>
                    <Label className={styles.label}>Work Classification</Label>
                    <CustomZDDropdown
                        applySelectedItems={(items) => {
                            updateFormState(LocalPayrollEmployeeModelKeys.classification, Array.isArray(items) ? items[0] : items);
                        }}
                        initialSelectedItems={employeeData.classification ? [employeeData.classification] : undefined}
                        openDropdownPlaceholder="Select Work Classification"
                        closedDropdownPlaceholder=""
                        options={AllEmployeeClassifications.map(opt => ({value: opt.value, label: opt.label}))}
                        isError={employeeData.workClassificationTouched && employeeData.classification === undefined ? true : undefined}
                        onBlur={() => {
                            return undefined;
                        }}
                        noSearchIcon
                        disabled={employeeLocked}
                    />
                </Field>

                <Row justifyContent="between">
                    <Col size={6}>
                        <Field className={styles.field}>
                            <Label className={styles.label}>Start Date</Label>
                            <PayrollDatePicker
                                maxDate={employeeData.terminationDate ?? ""}
                                dateValue={employeeData.startDate ?? ""}
                                disabled={employeeLocked}
                                onDateChange={(newDate) => {
                                    if (currentProperty?.isAcquisitionProperty) {
                                        updateFormState(LocalPayrollEmployeeModelKeys.startDate, newDate);
                                    } else {
                                        updateFormState(LocalPayrollEmployeeModelKeys.startDate, ensureDateInLocale(newDate)?.toISOString());
                                    }
                                }}
                            />
                        </Field>
                    </Col>
                    <Col size={6}>
                        <Field className={styles.field}>
                            <Label className={styles.label}>End Date</Label>
                            <PayrollDatePicker
                                minDate={employeeData.startDate ?? ""}
                                dateValue={employeeData.terminationDate ?? ""}
                                disabled={employeeLocked}
                                onDateChange={(newDate) => {
                                    if (currentProperty?.isAcquisitionProperty) {
                                        updateFormState(LocalPayrollEmployeeModelKeys.terminationDate, newDate);
                                    } else {
                                        updateFormState(LocalPayrollEmployeeModelKeys.terminationDate, ensureDateInLocale(newDate)?.toISOString());
                                    }
                                }}
                            />
                        </Field>
                    </Col>
                </Row>

                <h5
                    style={{
                        display: "inline-block",
                        marginBottom: "8px",
                        borderBottom: employeeData.propertiesTouched && employeeData.properties.length === 0 ? "1px solid red" : "none",
                    }}
                >
                    Properties (for cost allocation)
                </h5>
                <div style={{marginBottom: "16px"}}>
                    <Field>
                        <Checkbox
                            checked={employeeData.propertyAttributionUnitCount}
                            onChange={evt => updatePropertyAllocationUnitCount(evt.target.checked)}
                            disabled={employeeLocked}
                        >
                            <Label style={{fontWeight: "normal"}}>Allocate Costs by Unit Counts</Label>
                        </Checkbox>
                    </Field>
                </div>
                <div>
                    {propertyAttributions.map((pa, idx) => {
                        return <div key={idx} className={styles.propertyRow}>
                            <div className={styles.propertyInputs}>
                                <Field className={styles.propertyInputSelect}>
                                    <Label hidden>Property 1 (for cost allocation)</Label>
                                    <CustomZDDropdown
                                        applySelectedItems={items => setPropertyId(idx, items[0])}
                                        initialSelectedItems={pa.propertyId ? [pa.propertyId] : undefined}
                                        openDropdownPlaceholder="Select Property"
                                        closedDropdownPlaceholder=""
                                        disabled={
                                            currentProperty?.isAcquisitionProperty ||
                                            (pa.propertyId ? lockedProperties.reforecastLocked.includes(pa.propertyId) && lockedProperties.budgetLocked.includes(pa.propertyId) : false) ||
                                            employeeLocked
                                        }
                                        // previous behavior for 'disabled' was that the property had to be locked for both periods, but based on our current approach
                                        // employees are locked if a property is locked in either period
                                        options={propertiesData.map((opt => ({value: opt.id, label: opt.name, disabled: (lockedProperties.reforecastLocked.includes(opt.id) || lockedProperties.budgetLocked.includes(opt.id))})))}
                                        isError={employeeData.propertiesTouched && employeeData.properties.length === 0}
                                        isMulti={false}
                                    />
                                </Field>
                                {propertyAttributions.length > 1 && !employeeData.propertyAttributionUnitCount &&
                                    <Field className={styles.propertyInputAllocation}>
                                        <Label hidden>Allocation Percentage</Label>
                                        <Input
                                            type={"number"}
                                            value={pa.attributionPercentage}
                                            placeholder={"Allocation %"}
                                            onChange={evt => setAllocationPercentage(idx, evt.target.value)}
                                            disabled={pa.propertyId ? lockedProperties.reforecastLocked.includes(pa.propertyId) && lockedProperties.budgetLocked.includes(pa.propertyId) : false}
                                        />
                                    </Field>
                                }
                            </div>
                            {idx > 0 && !employeeLocked ?
                                <div className={styles.buttonRow}>
                                    <Button isBasic isDanger disabled={employeeLocked} onClick={() => removePropertyAllocation(idx)}>Remove</Button>
                                </div>:
                                <></>
                            }
                        </div>;
                    })}
                </div>
                {
                    !currentProperty?.isAcquisitionProperty && !employeeLocked &&
                        <div>
                            <Button isBasic className={styles.addButton} onClick={() => addPropertyAllocation()}>Add</Button>
                        </div>
                }
            </div>
        </div>
    );
}
